import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBell,
  FaToggleOn,
  FaToggleOff,
  FaImage,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";
import axios from "../../utils/axiosConfig";
import toast from "react-hot-toast";

const AnnouncementManager = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    link: "",
    linkText: "",
    targetAudience: "all",
    targetBranch: "",
    targetSemester: "",
    priority: 0,
    showAsPopup: false,
    expiresAt: "",
    image: null,
  });

  useEffect(() => {
    fetchAnnouncements();
    fetchFeatureStatus();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/announcements/admin/all", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements(response.data.announcements);
    } catch (error) {
      console.error("Fetch announcements error:", error);
      toast.error("Failed to fetch announcements");
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/announcements/admin/feature-status",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setFeatureEnabled(response.data.enabled);
    } catch (error) {
      console.error("Fetch feature status error:", error);
    }
  };

  const toggleFeature = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/announcements/admin/toggle-feature",
        { enabled: !featureEnabled },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setFeatureEnabled(!featureEnabled);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to toggle feature");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePriorityChange = (e) => {
    const value = e.target.value;
    if (value === "" || value === null || value === undefined) {
      setFormData({ ...formData, priority: 0 });
    } else {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        setFormData({ ...formData, priority: 0 });
      } else {
        setFormData({
          ...formData,
          priority: Math.min(10, Math.max(0, numValue)),
        });
      }
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.message) {
      toast.error("Title and message are required");
      return;
    }

    const data = new FormData();
    data.append("title", formData.title);
    data.append("message", formData.message);
    data.append("type", formData.type);
    if (formData.link) data.append("link", formData.link);
    if (formData.linkText) data.append("linkText", formData.linkText);
    data.append("targetAudience", formData.targetAudience);
    if (formData.targetBranch)
      data.append("targetBranch", formData.targetBranch);
    if (formData.targetSemester)
      data.append("targetSemester", formData.targetSemester);
    data.append("priority", formData.priority || 0);
    data.append("showAsPopup", formData.showAsPopup);
    if (formData.expiresAt) data.append("expiresAt", formData.expiresAt);
    if (formData.image) data.append("image", formData.image);

    try {
      const token = localStorage.getItem("token");
      if (editingAnnouncement) {
        await axios.put(
          `/api/announcements/admin/${editingAnnouncement._id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        toast.success("Announcement updated");
      } else {
        await axios.post("/api/announcements/admin/create", data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Announcement created");
      }
      fetchAnnouncements();
      resetForm();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/announcements/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      message: "",
      type: "info",
      link: "",
      linkText: "",
      targetAudience: "all",
      targetBranch: "",
      targetSemester: "",
      priority: 0,
      showAsPopup: false,
      expiresAt: "",
      image: null,
    });
    setImagePreview(null);
    setEditingAnnouncement(null);
  };

  const getTypeBadge = (type) => {
    const badges = {
      info: { bg: "#3b82f6", label: "Info" },
      warning: { bg: "#f59e0b", label: "Warning" },
      success: { bg: "#10b981", label: "Success" },
      event: { bg: "#8b5cf6", label: "Event" },
      promotion: { bg: "#ec4899", label: "Promotion" },
      emergency: { bg: "#ef4444", label: "Emergency" },
    };
    const badge = badges[type] || badges.info;
    return (
      <span
        style={{
          background: badge.bg,
          color: "white",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "10px",
        }}
      >
        {badge.label}
      </span>
    );
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const typeOptions = [
    "info",
    "warning",
    "success",
    "event",
    "promotion",
    "emergency",
  ];
  const audienceOptions = ["all", "branch", "semester"];
  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <FaSpinner
          style={{
            animation: "spin 1s linear infinite",
            fontSize: "2rem",
            color: "#8b5cf6",
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Feature Toggle Bar */}
      <div
        style={{
          background: getCardBackground(),
          borderRadius: "16px",
          padding: "1rem",
          marginBottom: "1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <FaBell size={20} color="#8b5cf6" />
          <div>
            <h4 style={{ margin: 0, color: getTextColor(), fontSize: "14px" }}>
              Announcement Feature
            </h4>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "11px",
                color: getTextSecondary(),
              }}
            >
              When disabled, no announcements will be shown to students
            </p>
          </div>
        </div>
        <button
          onClick={toggleFeature}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            background: featureEnabled ? "#10b981" : "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "30px",
            cursor: "pointer",
            fontWeight: "500",
            fontSize: "13px",
          }}
        >
          {featureEnabled ? (
            <FaToggleOn size={18} />
          ) : (
            <FaToggleOff size={18} />
          )}
          {featureEnabled ? "Feature Enabled" : "Feature Disabled"}
        </button>
      </div>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <h3 style={{ color: getTextColor(), margin: 0 }}>
          Announcements ({announcements.length})
        </h3>
        <button
          onClick={() => navigate("/admin/create-announcement")}
          style={{
            padding: "0.6rem 1.2rem",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "13px",
          }}
        >
          <FaPlus /> Create Announcement
        </button>
      </div>

      {/* Announcements List */}
      {announcements.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: getCardBackground(),
            borderRadius: "16px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaBell size={48} color={getTextSecondary()} />
          <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
            No announcements yet
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: "1rem" }}>
          {announcements.map((ann) => (
            <motion.div
              key={ann._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: getCardBackground(),
                borderRadius: "16px",
                padding: "1rem",
                border: `1px solid ${getBorderColor()}`,
                opacity: ann.isActive ? 1 : 0.6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginBottom: "8px",
                    }}
                  >
                    {getTypeBadge(ann.type)}
                    {ann.priority >= 7 && (
                      <span
                        style={{
                          background: "#ef4444",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                        }}
                      >
                        High Priority
                      </span>
                    )}
                    {!ann.isActive && (
                      <span
                        style={{
                          background: "#6c757d",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                        }}
                      >
                        Inactive
                      </span>
                    )}
                    {ann.targetAudience !== "all" && (
                      <span
                        style={{
                          background: "#8b5cf6",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                        }}
                      >
                        {ann.targetAudience === "branch"
                          ? `Branch: ${ann.targetBranch}`
                          : `Semester: ${ann.targetSemester}`}
                      </span>
                    )}
                  </div>
                  <h4
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "1rem",
                      color: getTextColor(),
                    }}
                  >
                    {ann.title}
                  </h4>
                  <p
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "13px",
                      color: getTextSecondary(),
                    }}
                  >
                    {ann.message}
                  </p>
                  {ann.imageUrl && (
                    <img
                      src={ann.imageUrl}
                      alt={ann.title}
                      style={{
                        maxWidth: "200px",
                        maxHeight: "100px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        marginBottom: "8px",
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      flexWrap: "wrap",
                      fontSize: "11px",
                      color: getTextSecondary(),
                    }}
                  >
                    <span>👁️ Seen by: {ann.seenBy?.length || 0} users</span>
                    <span>
                      ❌ Dismissed by: {ann.dismissedBy?.length || 0} users
                    </span>
                    <span>
                      📅 Created: {new Date(ann.createdAt).toLocaleDateString()}
                    </span>
                    {ann.expiresAt && (
                      <span>
                        ⏰ Expires:{" "}
                        {new Date(ann.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      setEditingAnnouncement(ann);
                      setFormData({
                        title: ann.title,
                        message: ann.message,
                        type: ann.type,
                        link: ann.link || "",
                        linkText: ann.linkText || "",
                        targetAudience: ann.targetAudience,
                        targetBranch: ann.targetBranch || "",
                        targetSemester: ann.targetSemester || "",
                        priority: ann.priority || 0,
                        showAsPopup: ann.showAsPopup,
                        expiresAt: ann.expiresAt
                          ? ann.expiresAt.split("T")[0]
                          : "",
                        image: null,
                      });
                      setImagePreview(ann.imageUrl);
                      setShowModal(true);
                    }}
                    style={{
                      padding: "0.4rem 0.8rem",
                      background: "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(ann._id)}
                    style={{
                      padding: "0.4rem 0.8rem",
                      background: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Floating Modal - Edit Only (Create now navigates to separate page) */}
      <AnimatePresence>
        {showModal && editingAnnouncement && (
          <>
            {/* Backdrop with blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(8px)",
                zIndex: 9998,
              }}
              onClick={() => setShowModal(false)}
            />

            {/* Modal Window - Edit Only */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                maxWidth: "520px",
                maxHeight: "90vh",
                background: getCardBackground(),
                borderRadius: "24px",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                border: `1px solid ${getBorderColor()}`,
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  padding: "18px 20px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaEdit size={18} color="white" />
                  </div>
                  <h3
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "1.1rem",
                      fontWeight: "600",
                    }}
                  >
                    ✏️ Edit Announcement
                  </h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "none",
                    borderRadius: "10px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "white",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(255,255,255,0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      "rgba(255,255,255,0.15)")
                  }
                >
                  <FaTimes size={16} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                <input
                  type="text"
                  placeholder="Title *"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontSize: "14px",
                    outline: "none",
                  }}
                />

                <textarea
                  placeholder="Message *"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontSize: "14px",
                    resize: "vertical",
                    outline: "none",
                  }}
                />

                <div style={{ display: "flex", gap: "12px" }}>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                      fontSize: "13px",
                    }}
                  >
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t.toUpperCase()}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.priority}
                    onChange={handlePriorityChange}
                    placeholder="Priority"
                    style={{
                      width: "85px",
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                      textAlign: "center",
                    }}
                  />
                </div>

                <select
                  value={formData.targetAudience}
                  onChange={(e) =>
                    setFormData({ ...formData, targetAudience: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {audienceOptions.map((a) => (
                    <option key={a} value={a}>
                      {a.toUpperCase()}
                    </option>
                  ))}
                </select>

                {formData.targetAudience === "branch" && (
                  <select
                    value={formData.targetBranch}
                    onChange={(e) =>
                      setFormData({ ...formData, targetBranch: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                )}

                {formData.targetAudience === "semester" && (
                  <select
                    value={formData.targetSemester}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        targetSemester: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="">Select Semester</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                      <option key={s} value={s}>
                        Semester {s}
                      </option>
                    ))}
                  </select>
                )}

                <div style={{ display: "flex", gap: "12px" }}>
                  <input
                    type="text"
                    placeholder="Link URL (optional)"
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Link Text"
                    value={formData.linkText}
                    onChange={(e) =>
                      setFormData({ ...formData, linkText: e.target.value })
                    }
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) =>
                      setFormData({ ...formData, expiresAt: e.target.value })
                    }
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                  <select
                    value={formData.showAsPopup}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        showAsPopup: e.target.value === "true",
                      })
                    }
                    style={{
                      width: "120px",
                      padding: "12px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="false">No</option>
                    <option value="true">Popup</option>
                  </select>
                </div>

                {/* Image Upload */}
                <div
                  style={{
                    border: `2px dashed ${getBorderColor()}`,
                    borderRadius: "12px",
                    padding: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => document.getElementById("imageInput").click()}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = "#8b5cf6")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = getBorderColor())
                  }
                >
                  <FaImage size={24} color={getTextSecondary()} />
                  <p
                    style={{
                      fontSize: "12px",
                      color: getTextSecondary(),
                      marginTop: "6px",
                    }}
                  >
                    {imagePreview
                      ? "Click to change image"
                      : "Upload an image (optional)"}
                  </p>
                  {imagePreview && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100px",
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                  )}
                  <input
                    id="imageInput"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "16px 20px",
                  borderTop: `1px solid ${getBorderColor()}`,
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  background: getCardBackground(),
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: "10px 24px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  style={{
                    padding: "10px 28px",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  Update
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnnouncementManager;
